<template>
  <div class="journey-flow-viewer" :style="{ '--sidebar-w': sidebarOpen ? '240px' : '40px', '--explorer-w': (currentJourneyJson ? explorerWidth : 0) + 'px' }">
    <builder-top-bar
      :has-journey="!!currentJourneyJson"
      :validating="validating"
      :validation-result="validationResult"
      :promoting="promoting"
      :promote-result="promoteResult"
      @validate="onValidateJourney"
      @promote="onPromoteToProduction"
    />

    <journey-sidebar
      ref="journeySidebar"
      :journey-keys="journeyKeys"
      :current-journey-key="currentJourneyKey"
      :versions="currentJourneyVersions"
      :active-version-number="currentVersionNumber"
      @journey-change="onJourneyChange($event)"
      @toggle-open="onSidebarToggle($event)"
      @create-new="onCreateNew()"
      @select-version="onSelectVersion($event)"
    />

    <div class="empty-hint" id="empty-hint">
      <div>
        <div class="big">🧭</div>
        Select a journey from the sidebar to visualize the flow
      </div>
    </div>

    <div class="canvas-wrap" id="wrap">
      <div class="canvas" id="canvas">
        <svg class="lines" id="svg"></svg>
      </div>
    </div>

    <div v-if="currentJourneyJson" class="view-controls">
      <span v-if="currentVersionNumber" class="vc-version-badge" title="Version currently shown">v{{ currentVersionNumber }}</span>
      <view-mode-switcher :value="viewMode" @change="onViewModeChange($event)" />
      <button class="vc-comments-btn" title="View all comments" @click="allCommentsOpen = true">
        💬 All comments
        <span v-if="totalCommentCount" class="vc-comments-count">{{ totalCommentCount }}</span>
      </button>
    </div>

    <all-comments-modal
      v-if="allCommentsOpen"
      :journey-key="currentJourneyKey"
      @close="allCommentsOpen = false"
      @select-stage="onSelectStageFromAllComments($event)"
    />

    <div v-if="currentJourneyJson" class="editor-actions">
      <button class="ea-btn ea-wa" title="Test in WhatsApp" @click="whatsappPreviewOpen = true">WhatsApp Preview</button>
      <button class="ea-icon-btn" title="Download JSON" @click="onDownload()">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
      </button>
      <button v-if="viewMode === 'client' && presentationData" class="ea-btn ea-wa" title="Download standalone Client View HTML" @click="onDownloadClientViewHtml()">Download HTML</button>
      <button class="ea-btn ea-save" title="Save New Version" @click="onOpenSaveVersion()">Save New Version</button>
    </div>

    <save-version-modal
      v-if="saveVersionModalOpen"
      :journey-name="currentJourneyKey"
      :next-version-number="nextVersionNumber"
      :previous-journey-json="previousVersionSnapshot"
      :current-journey-json="currentJourneyJson"
      @cancel="onCancelSaveVersion()"
      @save="onConfirmSaveVersion($event)"
    />

    <div class="canvas-controls">
      <button class="tb-btn" title="Reset layout" @click="onResetLayout()">↺</button>
      <button class="tb-btn" title="Fit to screen" @click="onFitScreen()">⛶</button>
      <div class="tb-sep"></div>
      <template v-if="viewMode !== 'client' && viewMode !== 'flow'">
        <button class="tb-btn" title="Collapse all" @click="onCollapseAll()">⊟</button>
        <button class="tb-btn" title="Expand all" @click="onExpandAll()">⊞</button>
        <div class="tb-sep"></div>
      </template>
      <button class="tb-btn" title="Zoom out" @click="onZoomOut()">−</button>
      <span class="zoom-info">{{ zoomPercent }}%</span>
      <button class="tb-btn" title="Zoom in" @click="onZoomIn()">+</button>
      <div class="tb-sep"></div>
      <button class="tb-btn" :class="{ active: scrollZoomEnabled }" title="Toggle scroll-to-zoom (off = scroll to pan)" @click="onToggleScrollZoom()">🔍</button>
    </div>

    <div v-if="hasSelection" class="selection-controls">
      <button class="mode-btn" :class="{ active: selMode === 'dim' }" title="Dim others on select" @click="onSelModeChange('dim')">Dim</button>
      <button class="mode-btn" :class="{ active: selMode === 'focus' }" title="Hide others on select" @click="onSelModeChange('focus')">Focus</button>
    </div>

    <div class="conn-tooltip" id="conn-tooltip"><span class="ct-icon">🖱️</span><span class="ct-text" id="ct-text"></span></div>

    <stage-explorer
      v-if="viewMode !== 'client' && currentJourneyJson"
      ref="stageExplorer"
      :stages="stageExplorerList"
      :selected-stage="selectedStageDetail"
      :lang-options="langOptions"
      :current-lang="currentLang"
      :journey-key="currentJourneyKey"
      @select-stage="onSelectStageFromExplorer($event)"
      @deselect="onDeselectStage()"
      @update-message="onUpdateStageMessage($event)"
      @update-topics="onUpdateStageTopics($event)"
      @width-change="onExplorerWidthChange($event)"
    />
    <phase-explorer
      v-else-if="currentJourneyJson"
      ref="phaseExplorer"
      :presentation="presentationData"
      :selected-phase="selectedPhaseDetail"
      :loading="presentationLoading"
      @select-phase="onSelectPhaseFromExplorer($event)"
      @deselect="onDeselectPhase()"
      @width-change="onExplorerWidthChange($event)"
    />

    <journey-chat
      ref="journeyChat"
      :current-journey-json="currentJourneyJson"
      @journey-generated="onJourneyGenerated($event)"
    />

    <div v-if="cardMenu.visible" class="card-ctx-menu" :style="{ left: cardMenu.x + 'px', top: cardMenu.y + 'px' }">
      <button class="card-ctx-item" @click="onAiEditStage()">✨ Change / Update using AI</button>
      <button class="card-ctx-item" @click="onAddComment()">💬 Add Comment</button>
    </div>

    <whatsapp-preview
      v-if="whatsappPreviewOpen"
      :journey="currentJourneyJson"
      :current-lang="currentLang"
      :lang-options="langOptions"
      @close="whatsappPreviewOpen = false"
    />
  </div>
</template>

<script>
import sampleJourneyData from '@/assets/journeys/aids_awareness_survey.json'
import { escapeHtml as esc } from './journey-flow/escapeHtml'
import { renderMessagePreview } from './journey-flow/messageCardPreview'
import { renderStagePreview } from './journey-flow/stageCardPreview'
import JourneySidebar from './journey-flow/JourneySidebar.vue'
import JourneyChat from './journey-flow/JourneyChat.vue'
import StageExplorer from './journey-flow/StageExplorer.vue'
import WhatsappPreview from './journey-flow/WhatsAppPreview.vue'
import PhaseExplorer from './journey-flow/PhaseExplorer.vue'
import SaveVersionModal from './journey-flow/SaveVersionModal.vue'
import BuilderTopBar from './journey-flow/BuilderTopBar.vue'
import ViewModeSwitcher from './journey-flow/ViewModeSwitcher.vue'
import AllCommentsModal from './journey-flow/AllCommentsModal.vue'
import { listVersions, latestVersionNumber, createVersion, renameJourney } from './journey-flow/journeyVersions'
import { buildClientViewHtml } from './journey-flow/exportClientViewHtml'
import { listAllComments, listComments, renameJourney as renameJourneyComments } from './journey-flow/journeyComments'
import api from '../../../utils/api'

export default {
  name: 'create-new-journey',
  components: { JourneySidebar, JourneyChat, StageExplorer, WhatsappPreview, PhaseExplorer, SaveVersionModal, BuilderTopBar, ViewModeSwitcher, AllCommentsModal },
  data () {
    return {
      journeyKeys: [],
      currentJourneyKey: '',
      viewMode: 'flow',
      selMode: 'dim',
      langOptions: [],
      currentLang: '',
      zoomPercent: 100,
      scrollZoomEnabled: false,
      cardMenu: { visible: false, x: 0, y: 0, stageName: '' },
      allCommentsOpen: false,
      presentationData: null,
      presentationLoading: false,
      stageExplorerList: [],
      hasSelection: false,
      selectedStageDetail: null,
      selectedPhaseDetail: null,
      sidebarOpen: false,
      explorerWidth: 40,
      currentJourneyJson: null,
      whatsappPreviewOpen: false,
      saveVersionModalOpen: false,
      currentJourneyVersions: [],
      currentVersionNumber: null,
      validating: false,
      validationResult: null,
      promoting: false,
      promoteResult: null
    }
  },
  computed: {
    nextVersionNumber () {
      return latestVersionNumber(this.currentJourneyKey) + 1
    },
    previousVersionSnapshot () {
      return this.currentJourneyVersions.length ? this.currentJourneyVersions[0].snapshot : null
    },
    totalCommentCount () {
      return this.currentJourneyKey ? listAllComments(this.currentJourneyKey).length : 0
    }
  },
  watch: {
    totalCommentCount () {
      if (this.refreshCommentBadges) this.refreshCommentBadges()
    }
  },
  methods: {
    onSidebarToggle (open) {
      this.sidebarOpen = open
    },
    onExplorerWidthChange (width) {
      this.explorerWidth = width
    },
    onAiEditStage () {
      const name = this.cardMenu.stageName
      this.cardMenu.visible = false
      if (this.$refs.journeyChat) this.$refs.journeyChat.openWithContext(name)
    },
    onAddComment () {
      const name = this.cardMenu.stageName
      this.cardMenu.visible = false
      this.onSelectStageFromExplorer(name)
      this.$nextTick(() => {
        if (this.$refs.stageExplorer) this.$refs.stageExplorer.activeTab = 'comments'
      })
    },
    onSelectStageFromAllComments (name) {
      this.allCommentsOpen = false
      this.onSelectStageFromExplorer(name)
      this.$nextTick(() => {
        if (this.$refs.stageExplorer) this.$refs.stageExplorer.activeTab = 'comments'
      })
    },
    onOpenSaveVersion () {
      if (!this.currentJourneyJson) return
      this.saveVersionModalOpen = true
    },
    onCancelSaveVersion () {
      this.saveVersionModalOpen = false
    },
    onConfirmSaveVersion (notes) {
      const version = createVersion(this.currentJourneyKey, { notes, journeyJson: this.currentJourneyJson })
      this.currentJourneyVersions = listVersions(this.currentJourneyKey)
      this.currentVersionNumber = version.versionNumber
      this.saveVersionModalOpen = false
    },
    onDownload () {
      if (!this.currentJourneyJson) return
      const name = (this.currentJourneyJson.name || this.currentJourneyKey || 'journey').replace(/[^a-z0-9_-]+/gi, '_')
      const blob = new Blob([JSON.stringify(this.currentJourneyJson, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${name}.json`
      a.click()
      URL.revokeObjectURL(url)
    },
    onDownloadClientViewHtml () {
      if (!this.presentationData) return
      const label = this.currentJourneyJson.name || this.currentJourneyKey || 'journey'
      const html = buildClientViewHtml(label, this.presentationData)
      const name = label.replace(/[^a-z0-9_-]+/gi, '_')
      const blob = new Blob([html], { type: 'text/html' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${name}_client_view.html`
      a.click()
      URL.revokeObjectURL(url)
    },
    async onValidateJourney () {
      if (!this.currentJourneyJson || this.validating) return
      this.validating = true
      this.validationResult = null
      this.promoteResult = null
      try {
        const response = await api.buildRequest('journey-builder-validate', 'POST', {
          journeyJson: { config: this.currentJourneyJson }
        })
        this.validationResult = { valid: !!response.data.valid, message: response.data.message }
      } catch (err) {
        this.validationResult = { valid: false, message: 'Validation request failed' }
      } finally {
        this.validating = false
      }
    },
    async onPromoteToProduction () {
      if (!this.currentJourneyJson || this.promoting) return
      if (!this.validationResult || !this.validationResult.valid) return
      this.promoting = true
      this.promoteResult = null
      try {
        const response = await api.buildRequest('journey-builder-save', 'POST', {
          journeyJson: { config: this.currentJourneyJson }
        })
        this.promoteResult = { success: !!response.data.success, message: response.data.message }
      } catch (err) {
        this.promoteResult = { success: false, message: 'Save request failed' }
      } finally {
        this.promoting = false
      }
    }
  },
  mounted () {
    const vm = this

    /* ===== Journey store ===== */
    const UNTITLED_KEY = 'untitled_journey'
    const journeyStore = {} // name -> data
    let JOURNEY = null

    /* ===== State ===== */
    let stageNames = []
    let allEdges = []
    let backEdges = new Set()
    let fwdAdj = {}
    let depth = {}
    let depthRows = {}
    let sortedDepths = []
    const CARD_W = 260
    const GAP_X = 40
    const GAP_Y = 44
    const cardPos = {}
    const cardEls = {}
    const connMap = {}
    const phaseCardPos = {}
    let phaseDepthRows = {}
    let phaseSortedDepths = []
    const canvasEl = document.getElementById('canvas')
    const svgEl = document.getElementById('svg')
    let currentScale = 1
    let panX = 0
    let panY = 0
    let isPanning = false
    let pStartX
    let pStartY
    let activeDrag = null

    function rebuildJourneyDropdown (selectKey) {
      vm.journeyKeys = Object.keys(journeyStore)
      vm.currentJourneyKey = selectKey
    }

    /* ===== Activate journey ===== */
    function activateJourney (key) {
      JOURNEY = journeyStore[key]
      vm.currentJourneyKey = key
      vm.currentJourneyJson = JOURNEY
      vm.currentJourneyVersions = listVersions(key)
      vm.currentVersionNumber = null
      vm.presentationData = null
      vm.selectedPhaseDetail = null
      vm.validationResult = null
      vm.promoteResult = null
      document.getElementById('empty-hint').classList.add('hidden')

      stageNames = Object.keys(JOURNEY.stages)
      allEdges = []; backEdges = new Set()
      Object.keys(cardPos).forEach(k => delete cardPos[k])
      Object.keys(cardEls).forEach(k => delete cardEls[k])
      Object.keys(connMap).forEach(k => delete connMap[k])

      // Languages
      const langs = new Set()
      Object.values(JOURNEY.stages).forEach(s => {
        (s.text || []).forEach(t => { Object.values(t).forEach(rl => Object.keys(rl).forEach(l => langs.add(l))) })
      })
      const langList = [...langs].sort()
      vm.currentLang = langList.includes('eng') ? 'eng' : (langList[0] || 'eng')
      vm.langOptions = langList.map(l => ({
        code: l,
        label: l === 'eng' ? 'English' : (l === 'hi' ? 'हिन्दी' : (l === 'th' ? 'ไทย' : l))
      }))

      // Edges
      stageNames.forEach(name => {
        const s = JOURNEY.stages[name]
        if (Array.isArray(s.next)) {
          const seen = new Set()
          ;(s.options || []).forEach((_, i) => {
            const d = s.next[i]; if (d && JOURNEY.stages[d] && !seen.has(d)) { seen.add(d); allEdges.push([name, d]) }
          })
        } else if (typeof s.next === 'string' && s.next && JOURNEY.stages[s.next]) {
          allEdges.push([name, s.next])
        }
      })

      // Back-edges
      backEdges = new Set()
      const visited = new Set()
      const inStack = new Set()
      function dfs (n) {
        visited.add(n); inStack.add(n)
        allEdges.forEach(([f, t]) => { if (f !== n) return; if (inStack.has(t)) { backEdges.add(f + '->' + t); return }; if (!visited.has(t)) dfs(t) })
        inStack.delete(n)
      }
      fwdAdj = {}
      depth = {}
      if (stageNames.length) {
        const root = stageNames.includes('uninitialized') ? 'uninitialized' : stageNames[0]
        dfs(root); stageNames.forEach(n => { if (!visited.has(n)) dfs(n) })

        // Depth
        stageNames.forEach(n => { fwdAdj[n] = [] })
        allEdges.forEach(([f, t]) => { if (!backEdges.has(f + '->' + t)) fwdAdj[f].push(t) })
        stageNames.forEach(n => { depth[n] = -1 }); depth[root] = 0
        const queue = [root]
        while (queue.length) { const n = queue.shift(); fwdAdj[n].forEach(t => { if (depth[t] < depth[n] + 1) { depth[t] = depth[n] + 1; queue.push(t) } }) }
        const maxD = Math.max(...Object.values(depth).filter(d => d >= 0), 0)
        stageNames.forEach(n => { if (depth[n] < 0) depth[n] = maxD + 1 })
      }

      depthRows = {}
      stageNames.forEach(n => { const d = depth[n]; if (!depthRows[d]) depthRows[d] = []; depthRows[d].push(n) })
      sortedDepths = Object.keys(depthRows).map(Number).sort((a, b) => a - b)

      vm.stageExplorerList = stageNames.map(name => {
        const s = JOURNEY.stages[name]
        const a = archOf(s)
        const desc = a === 'choice' ? `${(s.options || []).length} options`
          : a === 'text' ? 'free-text input'
          : a === 'end' ? 'END'
          : a === 'pf' ? 'routing' : 'info'
        return { name, arche: a, label: labelOf(a), emoji: iconOf(a), desc }
      })

      currentScale = 1; panX = 0; panY = 0; applyTransform()
      if (vm.viewMode === 'client') {
        fetchPresentation()
      } else {
        renderCards()
        requestAnimationFrame(() => { requestAnimationFrame(() => { drawLines(); fitToScreen(); setTimeout(drawLines, 120) }) })
      }
    }

    /* ===== Stage helpers ===== */
    function archOf (s) {
      if (s.nextApp || (s.stageName === 'end' && !s.next)) return 'end'
      if (s.processingFunctionConfig) return 'pf'
      if (s.input && Array.isArray(s.options) && s.options.length) return 'choice'
      if (s.input) return 'text'
      return 'info'
    }
    function labelOf (a) { return { info: 'Message', choice: 'Question', text: 'Text input', end: 'End', pf: 'Auto-route' }[a] || a }
    function iconOf (a) { return { info: '💬', choice: '❓', text: '✏️', end: '🏁', pf: '⚙️' }[a] || '🔹' }
    function commentBadgeHtml (name) {
      const count = listComments(vm.currentJourneyKey, name).length
      if (!count) return ''
      return `<span class="card-cmt-badge" title="${count} comment${count === 1 ? '' : 's'} · click to open" data-stage="${esc(name)}">💬 ${count}</span>`
    }
    function openStageComments (name) {
      if (selectedCardName !== name) selectCard(name)
      vm.$nextTick(() => {
        if (vm.$refs.stageExplorer) vm.$refs.stageExplorer.activeTab = 'comments'
      })
    }
    function wireCommentBadge (card, name) {
      const el = card.querySelector('.card-cmt-badge')
      if (!el) return
      el.addEventListener('mousedown', e => e.stopPropagation())
      el.addEventListener('click', e => { e.stopPropagation(); openStageComments(name) })
    }
    vm.refreshCommentBadges = function () {
      Object.entries(cardEls).forEach(([name, el]) => {
        const existing = el.querySelector('.card-cmt-badge')
        if (existing) existing.remove()
        const html = commentBadgeHtml(name)
        if (html) { el.insertAdjacentHTML('afterbegin', html); wireCommentBadge(el, name) }
      })
    }
    function msgText (stage, lang) {
      for (const t of (stage.text || [])) { for (const rl of Object.values(t)) { if (rl[lang]) return rl[lang] } }
      return ''
    }
    function cleanMsg (txt) {
      return txt.replace(/\{\{[^}]*\}\}/g, '').replace(/\[\[[^\]]*\]\]/g, '').replace(/\n{3,}/g, '\n\n').trim()
    }

    function buildStageDetail (name) {
      const s = JOURNEY.stages[name]
      const a = archOf(s)
      let options = null
      if (a === 'choice') {
        options = (s.options || []).map((opt, i) => ({ opt, next: Array.isArray(s.next) ? s.next[i] : null }))
      }
      const next = (typeof s.next === 'string' && s.next) ? s.next : null

      const stageLangs = new Set()
      ;(s.text || []).forEach(t => { Object.values(t).forEach(rl => Object.keys(rl).forEach(l => stageLangs.add(l))) })
      const availableLangs = [...stageLangs].sort()
      const messages = {}
      availableLangs.forEach(l => { messages[l] = cleanMsg(msgText(s, l)) })

      return {
        name,
        arche: a,
        label: labelOf(a),
        emoji: iconOf(a),
        stageType: s.stageType || 'traditional',
        mediaType: s.mediaType || 'text',
        inputType: s.input ? ((s.validations && s.validations.type) || 'Text') : null,
        endsChat: !!s.nextApp,
        availableLangs,
        messages,
        options,
        next,
        topics: s.topics || []
      }
    }

    // Message text may carry trailing {{quick-reply}}/[[input-hint]] template tokens
    // (rendered separately, stripped from the editable preview by cleanMsg) — preserve
    // them so editing the visible text doesn't drop that markup.
    function updateStageMessage (name, lang, text) {
      const s = JOURNEY && JOURNEY.stages[name]
      if (!s) return
      for (const entry of (s.text || [])) {
        for (const rl of Object.values(entry)) {
          if (!rl[lang]) continue
          const tail = (rl[lang].match(/(\{\{[^}]*\}\}|\[\[[^\]]*\]\])/g) || []).join('\n')
          rl[lang] = tail ? `${text}\n\n${tail}` : text
          if (vm.selectedStageDetail && vm.selectedStageDetail.name === name) {
            vm.selectedStageDetail = buildStageDetail(name)
          }
          vm.currentVersionNumber = null
          refreshCardBody(name)
          return
        }
      }
    }

    function updateStageTopics (name, topics) {
      const s = JOURNEY && JOURNEY.stages[name]
      if (!s) return
      s.topics = topics
      if (vm.selectedStageDetail && vm.selectedStageDetail.name === name) {
        vm.selectedStageDetail = buildStageDetail(name)
      }
      vm.currentVersionNumber = null
    }

    function refreshCardBody (name) {
      if (vm.viewMode === 'client') return
      const el = cardEls[name]
      const body = el && el.querySelector('.card-body')
      if (!body) return
      const s = JOURNEY.stages[name]
      body.innerHTML = vm.viewMode === 'stage' ? renderStagePreview(s) : renderMessagePreview(cleanMsg(msgText(s, vm.currentLang)))
      requestAnimationFrame(drawLines)
    }

    /* ===== Layout ===== */
    function reLayout (animate) {
      let curY = 60
      sortedDepths.forEach(d => {
        const row = depthRows[d]
        const totalW = row.length * CARD_W + (row.length - 1) * GAP_X
        const startX = -totalW / 2; let rowMaxH = 0
        row.forEach((n, i) => {
          const el = cardEls[n]; const h = el ? el.offsetHeight : 80; rowMaxH = Math.max(rowMaxH, h)
          cardPos[n] = { x: startX + i * (CARD_W + GAP_X), y: curY }
          if (el) {
            el.style.transition = animate ? 'top .3s cubic-bezier(.4,0,.2,1),left .3s cubic-bezier(.4,0,.2,1),border-color .12s' : 'none'
            el.style.left = cardPos[n].x + 'px'; el.style.top = cardPos[n].y + 'px'
          }
        })
        curY += rowMaxH + GAP_Y
      })
      if (animate) { let f = 0; const tick = () => { drawLines(); if (++f < 20) requestAnimationFrame(tick) }; requestAnimationFrame(tick) } else requestAnimationFrame(() => requestAnimationFrame(drawLines))
    }

    /* ===== Render cards ===== */
    function renderCards () {
      canvasEl.querySelectorAll('.card').forEach(c => c.remove())
      svgEl.innerHTML = ''; Object.keys(cardEls).forEach(k => delete cardEls[k])
      if (!JOURNEY) return

      const stages = JOURNEY.stages
      stageNames.forEach(name => {
        const s = stages[name]; const a = archOf(s)
        const rawTxt = msgText(s, vm.currentLang)
        const txt = cleanMsg(rawTxt)
        const cmtBadge = commentBadgeHtml(name)

        const card = document.createElement('div')
        card.className = 'card'; card.dataset.name = name
        card.style.left = '0px'; card.style.top = '0px'

        if (vm.viewMode === 'flow') {
          const shape = a === 'choice' ? 'hex' : a === 'pf' ? 'diamond' : a === 'end' ? 'pill' : 'rect'
          const descr = a === 'choice' ? `${(s.options || []).length} opts`
            : a === 'text' ? 'free text'
            : a === 'end' ? 'END'
            : a === 'pf' ? 'pf route' : 'msg'
          card.classList.add('flow-node-wrap')
          card.innerHTML = `<div class="flow-node flow-shape-${shape}">
            <div class="flow-node-label">
              <span class="flow-node-emoji">${iconOf(a)}</span><span class="flow-node-name">${esc(name)}</span>
              <div class="flow-node-descr">${esc(descr)}</div>
            </div>
          </div>${cmtBadge}`
          canvasEl.appendChild(card); cardEls[name] = card
          wireCommentBadge(card, name)

          card.addEventListener('mousedown', e => {
            if (e.button !== 0) return; e.stopPropagation()
            activeDrag = { card, startMX: e.clientX, startMY: e.clientY, startL: parseFloat(card.style.left), startT: parseFloat(card.style.top), moved: false }
          })
          return
        }

        let html = `${cmtBadge}<div class="card-head">
          <span class="icon">${iconOf(a)}</span>
          <span class="label">${esc(labelOf(a))}</span>
          <span class="stage-name">${esc(name)}</span>
          <span class="chevron">⌄</span>
        </div>`

        html += '<div class="card-body">'
        html += vm.viewMode === 'stage' ? renderStagePreview(s) : renderMessagePreview(txt)
        html += '</div>'

        let btns = ''
        if (a === 'choice') {
          const groups = {}
          const order = []
          ;(s.options || []).forEach((opt, i) => {
            const dest = Array.isArray(s.next) ? s.next[i] : null
            if (!dest) return; if (!groups[dest]) { groups[dest] = []; order.push(dest) }
            groups[dest].push(opt)
          })
          btns += '<div class="card-btns">'
          order.forEach(dest => {
            const opts = groups[dest]; const isBack = backEdges.has(name + '->' + dest)
            const lbl = opts.length > 3 ? opts.slice(0, 2).join(', ') + ' … +' + (opts.length - 2) + ' more' : opts.join(', ')
            if (isBack) btns += `<div class="card-loop">↩ ${esc(lbl)} → ${esc(dest)}</div>`
            else btns += `<div class="cbtn" data-from="${esc(name)}" data-to="${esc(dest)}"><span class="cdot"></span>${esc(lbl)}</div>`
          })
          btns += '</div>'
        }
        if (a === 'text' && !Array.isArray(s.options)) {
          btns += `<div class="card-btns"><div class="card-input">✏️ Free text → ${esc(typeof s.next === 'string' ? s.next : '…')}</div></div>`
        }
        if (a === 'pf') {
          const tgts = [...new Set(Array.isArray(s.next) ? s.next : [s.next].filter(Boolean))]
          btns += `<div class="card-btns"><div class="card-pf">⚙️ Route → ${tgts.map(t => esc(t)).join(', ')}</div></div>`
        }
        if (a !== 'choice') {
          let loops = ''
          allEdges.forEach(([f, t]) => { if (f === name && backEdges.has(f + '->' + t)) loops += `<div class="card-loop">↩ Loops to ${esc(t)}</div>` })
          if (loops) btns += `<div class="card-btns">${loops}</div>`
        }

        card.innerHTML = html + btns
        canvasEl.appendChild(card); cardEls[name] = card
        wireCommentBadge(card, name)

        card.querySelectorAll('.cbtn[data-to]').forEach(btn => {
          const toN = btn.dataset.to
          const fromN = btn.dataset.from
          btn.addEventListener('mouseenter', () => {
            btn.classList.add('hl'); const tgt = cardEls[toN]; if (tgt) tgt.classList.add('hl')
            const c = connMap[fromN + '->' + toN]
            if (c) { c.path.classList.add('hl'); c.path.setAttribute('marker-end', 'url(#ah-hl)'); c.dot.setAttribute('fill', 'var(--line-hl)') }
          })
          btn.addEventListener('mouseleave', () => {
            btn.classList.remove('hl'); const tgt = cardEls[toN]; if (tgt) tgt.classList.remove('hl')
            const c = connMap[fromN + '->' + toN]
            if (c) { c.path.classList.remove('hl'); c.path.setAttribute('marker-end', 'url(#ah)'); c.dot.setAttribute('fill', 'var(--line)') }
          })
        })

        card.querySelector('.card-head').addEventListener('mousedown', e => {
          if (e.button !== 0) return; e.stopPropagation()
          activeDrag = { card, startMX: e.clientX, startMY: e.clientY, startL: parseFloat(card.style.left), startT: parseFloat(card.style.top), moved: false }
        })

        const body = card.querySelector('.card-body')
        if (body) {
          body.addEventListener('click', e => {
            e.stopPropagation()
            selectCard(name)
          })
        }
      })

      reLayout(false)
    }

    /* ===== Draw lines ===== */
    function drawLines () {
      if (vm.viewMode === 'client') return
      svgEl.innerHTML = ''; Object.keys(connMap).forEach(k => delete connMap[k])
      const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
      defs.innerHTML = '<marker id="ah" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto"><path d="M0 1L6 4L0 7" fill="none" stroke="var(--line)" stroke-width="1.2"/></marker><marker id="ah-hl" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto"><path d="M0 1L6 4L0 7" fill="none" stroke="var(--line-hl)" stroke-width="1.2"/></marker>'
      svgEl.appendChild(defs)
      if (!JOURNEY) return

      stageNames.forEach(name => {
        const s = JOURNEY.stages[name]; const a = archOf(s); const card = cardEls[name]; if (!card) return
        if (a === 'choice') {
          if (vm.viewMode === 'flow') {
            const groups = {}
            const order = []
            ;(s.options || []).forEach((opt, i) => {
              const dest = Array.isArray(s.next) ? s.next[i] : null
              if (!dest || backEdges.has(name + '->' + dest)) return
              if (!groups[dest]) { groups[dest] = []; order.push(dest) }
              groups[dest].push(opt)
            })
            order.forEach(dest => {
              const tc = cardEls[dest]; if (tc) drawConn(card, tc, name, dest, true, groups[dest].join(', '))
            })
          } else {
            card.querySelectorAll('.cbtn[data-to]').forEach(btn => {
              const tc = cardEls[btn.dataset.to]; if (tc) drawConn(btn, tc, name, btn.dataset.to, false)
            })
          }
        } else {
          let targets = []
          if (typeof s.next === 'string' && s.next && !backEdges.has(name + '->' + s.next)) targets.push(s.next)
          if (a === 'pf' && Array.isArray(s.next)) { [...new Set(s.next)].forEach(t => { if (t && !backEdges.has(name + '->' + t)) targets.push(t) }) }
          targets.forEach(t => { const tc = cardEls[t]; if (tc) drawConn(card, tc, name, t, true) })
        }
      })
      if (selectedCardName) selectCard(selectedCardName, true)
    }

    /* ===== Connection tooltip ===== */
    const connTooltip = document.getElementById('conn-tooltip')
    const ctText = document.getElementById('ct-text')
    function showConnTooltip (e, text) {
      ctText.textContent = text
      connTooltip.classList.add('show')
      positionConnTooltip(e)
    }
    function positionConnTooltip (e) {
      const pad = 16
      let x = e.clientX + pad
      let y = e.clientY + pad
      const tw = connTooltip.offsetWidth || 160
      const th = connTooltip.offsetHeight || 28
      if (x + tw > window.innerWidth - 8) x = e.clientX - tw - pad
      if (y + th > window.innerHeight - 8) y = e.clientY - th - pad
      connTooltip.style.left = x + 'px'; connTooltip.style.top = y + 'px'
    }
    function hideConnTooltip () { connTooltip.classList.remove('show') }

    function connLabelFor (fromEl, fromName, toName, fromCard) {
      if (!fromCard) return fromEl.textContent.trim()
      const s = JOURNEY.stages[fromName]; const a = archOf(s)
      if (a === 'text') return 'Free text input → ' + toName
      if (a === 'pf') return 'Auto-route → ' + toName
      return 'Continues to → ' + toName
    }

    function drawConn (fromEl, toEl, fromName, toName, fromCard, labelOverride) {
      const fR = fromEl.getBoundingClientRect()
      const tR = toEl.getBoundingClientRect()
      const cR = canvasEl.getBoundingClientRect()
      const s = currentScale
      const toX = (tR.left + tR.width / 2 - cR.left) / s
      const toY = (tR.top - cR.top) / s
      let x1, y1, d
      if (fromCard) {
        x1 = (fR.left + fR.width / 2 - cR.left) / s; y1 = (fR.bottom - cR.top) / s
        const cp = Math.max(40, Math.abs(toY - y1) * 0.35)
        d = `M${x1} ${y1} C${x1} ${y1 + cp},${toX} ${toY - cp},${toX} ${toY}`
      } else {
        const fcx = (fR.left + fR.width / 2 - cR.left) / s; const goLeft = toX < fcx
        x1 = goLeft ? (fR.left - cR.left) / s - 4 : (fR.right - cR.left) / s + 4
        y1 = (fR.top + fR.height / 2 - cR.top) / s
        const dx = toX - x1
        const dy = toY - y1
        const cpX = goLeft ? Math.min(-50, dx * 0.4) : Math.max(50, dx * 0.4)
        const cpY = Math.max(40, Math.abs(dy) * 0.35)
        d = `M${x1} ${y1} C${x1 + cpX} ${y1},${toX} ${toY - cpY},${toX} ${toY}`
      }
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      path.setAttribute('d', d); path.setAttribute('marker-end', 'url(#ah)')
      path.dataset.from = fromName; path.dataset.to = toName
      const hit = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      hit.setAttribute('d', d)
      hit.setAttribute('fill', 'none')
      hit.setAttribute('stroke', 'transparent')
      const hitW = Math.min(60, Math.max(16, 22 / currentScale))
      hit.setAttribute('stroke-width', String(hitW))
      hit.style.cursor = 'pointer'
      svgEl.appendChild(hit)
      svgEl.appendChild(path)

      const label = labelOverride || connLabelFor(fromEl, fromName, toName, fromCard)
      if (vm.viewMode === 'flow' && labelOverride) {
        const mid = path.getPointAtLength(path.getTotalLength() / 2)
        const labelEl = document.createElementNS('http://www.w3.org/2000/svg', 'text')
        labelEl.textContent = labelOverride
        labelEl.setAttribute('x', mid.x); labelEl.setAttribute('y', mid.y)
        labelEl.setAttribute('class', 'flow-edge-label')
        svgEl.appendChild(labelEl)
        try {
          const bbox = labelEl.getBBox()
          const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
          bg.setAttribute('x', bbox.x - 4); bg.setAttribute('y', bbox.y - 2)
          bg.setAttribute('width', bbox.width + 8); bg.setAttribute('height', bbox.height + 4)
          bg.setAttribute('rx', 4); bg.setAttribute('class', 'flow-edge-label-bg')
          svgEl.insertBefore(bg, labelEl)
        } catch (err) { /* getBBox can fail if not yet laid out — label still shows without a backdrop */ }
      }
      const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
      hit.addEventListener('mouseenter', e => {
        path.classList.add('hl'); path.setAttribute('marker-end', 'url(#ah-hl)')
        dot.setAttribute('fill', 'var(--line-hl)')
        const tc = cardEls[toName]; if (tc) tc.classList.add('hl')
        showConnTooltip(e, label)
      })
      hit.addEventListener('mousemove', positionConnTooltip)
      hit.addEventListener('mouseleave', () => {
        path.classList.remove('hl'); path.setAttribute('marker-end', 'url(#ah)')
        dot.setAttribute('fill', 'var(--line)')
        const tc = cardEls[toName]; if (tc) tc.classList.remove('hl')
        hideConnTooltip()
      })
      svgEl.appendChild(path)
      dot.setAttribute('cx', x1); dot.setAttribute('cy', y1); dot.setAttribute('r', '3.5')
      dot.setAttribute('fill', 'var(--line)'); dot.setAttribute('stroke', 'var(--card)'); dot.setAttribute('stroke-width', '2')
      dot.dataset.from = fromName; dot.dataset.to = toName
      svgEl.appendChild(dot)
      connMap[fromName + '->' + toName] = { path, dot }
      path.addEventListener('mouseenter', e => {
        path.classList.add('hl'); path.setAttribute('marker-end', 'url(#ah-hl)')
        dot.setAttribute('fill', 'var(--line-hl)')
        const tc = cardEls[toName]; if (tc) tc.classList.add('hl')
        showConnTooltip(e, label)
      })
      path.addEventListener('mousemove', positionConnTooltip)
      path.addEventListener('mouseleave', () => {
        path.classList.remove('hl'); path.setAttribute('marker-end', 'url(#ah)')
        dot.setAttribute('fill', 'var(--line)')
        const tc = cardEls[toName]; if (tc) tc.classList.remove('hl')
        hideConnTooltip()
      })
      path.style.pointerEvents = 'stroke'
    }

    /* ===== Pan, zoom, drag ===== */
    const wrap = document.getElementById('wrap')
    function applyTransform () {
      canvasEl.style.transform = `translate(${panX}px,${panY}px) scale(${currentScale})`
      vm.zoomPercent = Math.round(currentScale * 100)
    }
    document.addEventListener('click', e => {
      if (
        !e.target.closest('.card') &&
        !e.target.closest('.toolbar') &&
        !e.target.closest('.stage-explorer') &&
        !e.target.closest('.phase-explorer') &&
        !e.target.closest('.selection-controls') &&
        !e.target.closest('.canvas-controls')
      ) {
        if (selectedCardName) clearSelection()
        if (selectedPhaseName) clearPhaseSelection()
      }
    }, true)
    wrap.addEventListener('mousedown', e => {
      if (e.target.closest('.card')) return
      isPanning = true; pStartX = e.clientX - panX; pStartY = e.clientY - panY; wrap.classList.add('grabbing')
    })
    wrap.addEventListener('dblclick', e => {
      if (e.target.closest('.card')) return
      const sidebar = vm.$refs.journeySidebar
      const explorer = vm.$refs.stageExplorer || vm.$refs.phaseExplorer
      if (!sidebar || !explorer) return
      const bothOpen = !sidebar.collapsed && !explorer.collapsed
      sidebar.setCollapsed(bothOpen)
      explorer.setCollapsed(bothOpen)
    })
    wrap.addEventListener('contextmenu', e => {
      const card = e.target.closest('.card')
      if (!card || vm.viewMode === 'client') return
      e.preventDefault()
      vm.cardMenu = { visible: true, x: e.clientX, y: e.clientY, stageName: card.dataset.name }
    })
    document.addEventListener('click', e => {
      if (vm.cardMenu.visible && !e.target.closest('.card-ctx-menu')) vm.cardMenu.visible = false
    }, true)
    window.addEventListener('mousemove', e => {
      if (activeDrag) {
        const dx = (e.clientX - activeDrag.startMX) / currentScale
        const dy = (e.clientY - activeDrag.startMY) / currentScale
        if (!activeDrag.moved && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) { activeDrag.moved = true; activeDrag.card.classList.add('dragging') }
        if (activeDrag.moved) {
          activeDrag.card.style.left = (activeDrag.startL + dx) + 'px'; activeDrag.card.style.top = (activeDrag.startT + dy) + 'px'
          if (vm.viewMode === 'client') drawPhaseLines(); else drawLines()
        }
        return
      }
      if (!isPanning) return
      panX = e.clientX - pStartX; panY = e.clientY - pStartY; applyTransform()
    })
    window.addEventListener('mouseup', () => {
      if (activeDrag) {
        activeDrag.card.classList.remove('dragging')
        if (!activeDrag.moved) {
          if (vm.viewMode === 'flow') selectCard(activeDrag.card.dataset.name)
          else activeDrag.card.classList.toggle('collapsed')
          if (vm.viewMode === 'client') drawPhaseLines()
          else reLayout(true)
        } else if (vm.viewMode === 'client') {
          drawPhaseLines()
        } else {
          drawLines()
        }
        activeDrag = null; return
      }
      isPanning = false; wrap.classList.remove('grabbing')
    })
    wrap.addEventListener('wheel', e => {
      e.preventDefault()
      if (!vm.scrollZoomEnabled) {
        const dx = e.deltaX || (e.shiftKey ? e.deltaY : 0)
        const dy = e.shiftKey ? 0 : e.deltaY
        panX -= dx; panY -= dy; applyTransform()
        return
      }
      const delta = e.deltaY > 0 ? -0.08 : 0.08
      const ns = Math.max(0.15, Math.min(2.5, currentScale + delta))
      const r = wrap.getBoundingClientRect()
      const mx = e.clientX - r.left
      const my = e.clientY - r.top
      panX = mx - (mx - panX) * (ns / currentScale); panY = my - (my - panY) * (ns / currentScale)
      currentScale = ns; applyTransform()
      // Lines live inside the same transformed .canvas element, so they scale/pan
      // for free — redrawing them here was just adding lag, not fixing anything.
    }, { passive: false })

    function zoomBy (d) { currentScale = Math.max(0.15, Math.min(2.5, currentScale + d)); applyTransform() }

    /* ===== Card selection: highlight clicked + neighbors ===== */
    let selectedCardName = null

    function centerCardIfNeeded (name) {
      const el = cardEls[name]
      const pos = cardPos[name]
      if (!el || !pos) return
      const cardH = el.offsetHeight || 80
      const ww = wrap.clientWidth
      const wh = wrap.clientHeight
      const margin = 40
      const left = panX + pos.x * currentScale
      const top = panY + pos.y * currentScale
      const right = left + CARD_W * currentScale
      const bottom = top + cardH * currentScale
      const visible = left >= margin && top >= margin && right <= ww - margin && bottom <= wh - margin
      if (visible) return
      panX = ww / 2 - (pos.x + CARD_W / 2) * currentScale
      panY = wh / 2 - (pos.y + cardH / 2) * currentScale
      applyTransform()
      requestAnimationFrame(drawLines)
    }

    function selectCard (name, force) {
      if (!force) clearSelection()
      if (!force && selectedCardName === name) { selectedCardName = null; vm.hasSelection = false; vm.selectedStageDetail = null; return }
      selectedCardName = name
      vm.hasSelection = true
      vm.selectedStageDetail = buildStageDetail(name)
      centerCardIfNeeded(name)

      const children = new Set()
      const parents = new Set()
      allEdges.forEach(([f, t]) => {
        if (backEdges.has(f + '->' + t)) return
        if (f === name) children.add(t)
        if (t === name) parents.add(f)
      })
      const involved = new Set([name, ...children, ...parents])

      Object.entries(cardEls).forEach(([n, el]) => {
        if (n === name) el.classList.add('selected')
        else if (involved.has(n)) el.classList.add('neighbor')
        else if (vm.selMode === 'focus') el.classList.add('hidden-card')
        else el.classList.add('dimmed')
      })

      const connKeys = new Set()
      parents.forEach(p => connKeys.add(p + '->' + name))
      children.forEach(c => connKeys.add(name + '->' + c))

      svgEl.querySelectorAll('path[data-from]').forEach(p => {
        const key = p.dataset.from + '->' + p.dataset.to
        if (connKeys.has(key)) p.classList.add('conn-hl')
        else if (vm.selMode === 'focus') p.style.display = 'none'
        else p.style.opacity = '0.12'
      })
      svgEl.querySelectorAll('circle[data-from]').forEach(c => {
        const key = c.dataset.from + '->' + c.dataset.to
        if (connKeys.has(key)) c.classList.add('conn-hl')
        else if (vm.selMode === 'focus') c.style.display = 'none'
        else c.style.opacity = '0.12'
      })
      if (vm.selMode === 'focus') {
        svgEl.querySelectorAll('path:not([data-from])').forEach(p => {
          if (p.getAttribute('stroke') === 'transparent') p.style.display = 'none'
        })
      }
    }

    function clearSelection () {
      selectedCardName = null
      vm.hasSelection = false
      vm.selectedStageDetail = null
      Object.values(cardEls).forEach(el => { el.classList.remove('selected', 'neighbor', 'dimmed', 'hidden-card') })
      svgEl.querySelectorAll('.conn-hl').forEach(el => el.classList.remove('conn-hl'))
      svgEl.querySelectorAll('path,circle').forEach(el => { el.style.opacity = ''; el.style.display = '' })
      drawLines()
    }
    /* ===== Phase cards (Client View) ===== */
    const phaseCardEls = {}
    let selectedPhaseName = null

    function phasePalette (color) {
      const map = {
        pink: { c: '#E91E8C', light: '#FCE4EC' },
        blue: { c: '#1565C0', light: '#E3F2FD' },
        green: { c: '#2E7D32', light: '#E8F5E9' },
        orange: { c: '#F57C00', light: '#FFF3E0' },
        purple: { c: '#6A1B9A', light: '#F3E5F5' },
        teal: { c: '#00897B', light: '#E0F2F1' },
        slate: { c: '#37474F', light: '#ECEFF1' },
        amber: { c: '#F9A825', light: '#FFF8E1' }
      }
      return map[color] || map.slate
    }

    function fetchPresentation () {
      if (!JOURNEY || !stageNames.length) return
      vm.presentationLoading = true
      api.buildRequest('journey-to-presentation', 'POST', { journeyJson: JOURNEY }).then(response => {
        if (response.data && response.data.presentation) {
          vm.presentationData = response.data.presentation
          if (vm.viewMode === 'client') renderPhaseCards()
        }
      }).catch(err => {
        console.error('[journey-to-presentation]', err)
      }).finally(() => {
        vm.presentationLoading = false
      })
    }

    function phaseNeighbors (id) {
      const data = vm.presentationData
      const phases = (data && data.phases) || []
      const happy = (data && data.happy_path) || []
      const children = new Set()
      const parents = new Set()
      happy.forEach((pid, i) => {
        if (pid !== id) return
        if (happy[i + 1]) children.add(happy[i + 1])
        if (happy[i - 1]) parents.add(happy[i - 1])
      })
      phases.forEach(p => {
        (p.branches_to || []).forEach(b => {
          if (p.id === id) children.add(b.target_phase)
          if (b.target_phase === id) parents.add(p.id)
        })
      })
      return { children, parents }
    }

    function selectPhaseCard (id, force) {
      if (!force) clearPhaseSelection()
      if (!force && selectedPhaseName === id) { selectedPhaseName = null; vm.hasSelection = false; vm.selectedPhaseDetail = null; return }
      selectedPhaseName = id
      vm.hasSelection = true
      const phases = (vm.presentationData && vm.presentationData.phases) || []
      vm.selectedPhaseDetail = phases.find(p => p.id === id) || null
      const { children, parents } = phaseNeighbors(id)
      const involved = new Set([id, ...children, ...parents])
      Object.entries(phaseCardEls).forEach(([n, el]) => {
        el.classList.remove('selected', 'neighbor', 'dimmed', 'hidden-card')
        if (n === id) el.classList.add('selected')
        else if (involved.has(n)) el.classList.add('neighbor')
        else if (vm.selMode === 'focus') el.classList.add('hidden-card')
        else el.classList.add('dimmed')
      })
    }

    function clearPhaseSelection () {
      selectedPhaseName = null
      vm.hasSelection = false
      vm.selectedPhaseDetail = null
      Object.values(phaseCardEls).forEach(el => el.classList.remove('selected', 'neighbor', 'dimmed', 'hidden-card'))
    }

    // Positions phases on a BFS-depth grid (same approach as the stage/flow views'
    // reLayout) instead of one straight column, so branches_to targets fan out
    // side-by-side rather than reading as a single-direction flow.
    function computePhaseLayout (data) {
      const ids = (data.phases || []).map(p => p.id)
      const idSet = new Set(ids)
      const happy = data.happy_path || []
      const edgeSet = new Set()
      const edges = []
      function addEdge (f, t) {
        if (!idSet.has(f) || !idSet.has(t) || f === t) return
        const k = f + '->' + t
        if (edgeSet.has(k)) return
        edgeSet.add(k); edges.push([f, t])
      }
      for (let i = 0; i < happy.length - 1; i++) addEdge(happy[i], happy[i + 1])
      ;(data.phases || []).forEach(p => { (p.branches_to || []).forEach(b => addEdge(p.id, b.target_phase)) })

      const backEdges = new Set()
      const visited = new Set()
      const inStack = new Set()
      function dfs (n) {
        visited.add(n); inStack.add(n)
        edges.forEach(([f, t]) => {
          if (f !== n) return
          if (inStack.has(t)) { backEdges.add(f + '->' + t); return }
          if (!visited.has(t)) dfs(t)
        })
        inStack.delete(n)
      }
      const root = (happy[0] && idSet.has(happy[0])) ? happy[0] : ids[0]
      if (root) dfs(root)
      ids.forEach(n => { if (!visited.has(n)) dfs(n) })

      const fwdAdj = {}
      ids.forEach(n => { fwdAdj[n] = [] })
      edges.forEach(([f, t]) => { if (!backEdges.has(f + '->' + t)) fwdAdj[f].push(t) })

      const depth = {}
      ids.forEach(n => { depth[n] = -1 })
      if (root) {
        depth[root] = 0
        const queue = [root]
        while (queue.length) {
          const n = queue.shift()
          fwdAdj[n].forEach(t => { if (depth[t] < depth[n] + 1) { depth[t] = depth[n] + 1; queue.push(t) } })
        }
      }
      const maxD = Math.max(...Object.values(depth).filter(d => d >= 0), 0)
      ids.forEach(n => { if (depth[n] < 0) depth[n] = maxD + 1 })

      phaseDepthRows = {}
      ids.forEach(n => { const d = depth[n]; if (!phaseDepthRows[d]) phaseDepthRows[d] = []; phaseDepthRows[d].push(n) })
      phaseSortedDepths = Object.keys(phaseDepthRows).map(Number).sort((a, b) => a - b)
    }

    function rePhaseLayout (animate) {
      let curY = 60
      phaseSortedDepths.forEach(d => {
        const row = phaseDepthRows[d]
        const totalW = row.length * CARD_W + (row.length - 1) * GAP_X
        const startX = -totalW / 2; let rowMaxH = 0
        row.forEach((id, i) => {
          const el = phaseCardEls[id]; const h = el ? el.offsetHeight : 100; rowMaxH = Math.max(rowMaxH, h)
          phaseCardPos[id] = { x: startX + i * (CARD_W + GAP_X), y: curY }
          if (el) {
            el.style.transition = animate ? 'top .3s cubic-bezier(.4,0,.2,1),left .3s cubic-bezier(.4,0,.2,1),border-color .12s' : 'none'
            el.style.left = phaseCardPos[id].x + 'px'; el.style.top = phaseCardPos[id].y + 'px'
          }
        })
        curY += rowMaxH + GAP_Y
      })
      if (animate) { let f = 0; const tick = () => { drawPhaseLines(); if (++f < 20) requestAnimationFrame(tick) }; requestAnimationFrame(tick) }
    }

    function renderPhaseCards () {
      canvasEl.querySelectorAll('.card').forEach(c => c.remove())
      svgEl.innerHTML = ''
      Object.keys(phaseCardEls).forEach(k => delete phaseCardEls[k])
      selectedPhaseName = null
      vm.hasSelection = false
      vm.selectedPhaseDetail = null
      const data = vm.presentationData
      if (!data) return
      const phases = data.phases || []
      computePhaseLayout(data)

      phases.forEach(phase => {
        const pal = phasePalette(phase.color)
        const stageCount = (phase.stages || []).length
        const card = document.createElement('div')
        card.className = 'card phase-card'; card.dataset.name = phase.id
        card.style.left = '0px'; card.style.top = '0px'
        card.style.borderColor = pal.c

        card.innerHTML = `<div class="card-head" style="background:${pal.light}">
          <span class="label" style="color:${pal.c}">${esc(phase.type || '')}</span>
          <span class="stage-name">${esc(phase.id)}</span>
          <span class="chevron">⌄</span>
        </div>
        <div class="card-body">
          <div class="phase-card-title">${esc(phase.label)}</div>
          <div class="phase-card-desc">${esc(phase.description || '')}</div>
          <div class="phase-card-count">${stageCount} stage${stageCount === 1 ? '' : 's'}</div>
        </div>`
        canvasEl.appendChild(card)
        phaseCardEls[phase.id] = card

        card.querySelector('.card-head').addEventListener('mousedown', e => {
          if (e.button !== 0) return; e.stopPropagation()
          activeDrag = { card, startMX: e.clientX, startMY: e.clientY, startL: parseFloat(card.style.left), startT: parseFloat(card.style.top), moved: false }
        })
        card.querySelector('.card-body').addEventListener('click', e => {
          e.stopPropagation(); selectPhaseCard(phase.id)
        })
      })

      rePhaseLayout(false)
      requestAnimationFrame(() => { requestAnimationFrame(() => { drawPhaseLines(); fitToScreen(); setTimeout(drawPhaseLines, 120) }) })
    }

    function drawPhaseLines () {
      svgEl.innerHTML = ''
      const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
      defs.innerHTML = '<marker id="ah" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto"><path d="M0 1L6 4L0 7" fill="none" stroke="var(--line)" stroke-width="1.2"/></marker>'
      svgEl.appendChild(defs)
      const data = vm.presentationData
      if (!data) return
      const phases = data.phases || []
      const happy = data.happy_path || []
      for (let i = 0; i < happy.length - 1; i++) {
        const fromEl = phaseCardEls[happy[i]]
        const toEl = phaseCardEls[happy[i + 1]]
        if (fromEl && toEl) drawPhaseConn(fromEl, toEl, false)
      }
      phases.forEach(p => {
        (p.branches_to || []).forEach(b => {
          const hi = happy.indexOf(p.id)
          if (hi >= 0 && happy[hi + 1] === b.target_phase) return
          const fromEl = phaseCardEls[p.id]
          const toEl = phaseCardEls[b.target_phase]
          if (fromEl && toEl) drawPhaseConn(fromEl, toEl, true)
        })
      })
    }

    function drawPhaseConn (fromEl, toEl, dashed) {
      const fR = fromEl.getBoundingClientRect()
      const tR = toEl.getBoundingClientRect()
      const cR = canvasEl.getBoundingClientRect()
      const s = currentScale
      const x1 = (fR.left + fR.width / 2 - cR.left) / s
      const y1 = (fR.bottom - cR.top) / s
      const x2 = (tR.left + tR.width / 2 - cR.left) / s
      const y2 = (tR.top - cR.top) / s
      const cp = Math.max(40, Math.abs(y2 - y1) * 0.35)
      const d = `M${x1} ${y1} C${x1} ${y1 + cp},${x2} ${y2 - cp},${x2} ${y2}`
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      path.setAttribute('d', d)
      path.setAttribute('marker-end', 'url(#ah)')
      if (dashed) path.setAttribute('stroke-dasharray', '5,4')
      svgEl.appendChild(path)
    }

    function toggleAll (c) { Object.values(cardEls).forEach(el => el.classList.toggle('collapsed', c)); reLayout(true) }
    function fitToScreen () {
      const cards = canvasEl.querySelectorAll('.card'); if (!cards.length) return
      let minX = 1e9
      let minY = 1e9
      let maxX = -1e9
      let maxY = -1e9
      cards.forEach(c => {
        const x = parseFloat(c.style.left)
        const y = parseFloat(c.style.top)
        minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x + 260); maxY = Math.max(maxY, y + (c.offsetHeight || 80))
      })
      const w = maxX - minX + 80
      const h = maxY - minY + 120
      const ww = wrap.clientWidth
      const wh = wrap.clientHeight - 50
      currentScale = Math.max(0.2, Math.min(1.5, Math.min(ww / w, wh / h)))
      panX = (ww - w * currentScale) / 2 - minX * currentScale; panY = 50 + (wh - h * currentScale) / 2 - minY * currentScale + 20
      applyTransform()
      requestAnimationFrame(() => { if (vm.viewMode === 'client') drawPhaseLines(); else drawLines() })
    }

    /* ===== Toolbar event handlers ===== */
    vm.onJourneyChange = function (key) {
      if (journeyStore[key]) activateJourney(key)
    }
    vm.onSelectVersion = function (version) {
      const key = vm.currentJourneyKey
      if (!journeyStore[key]) return
      journeyStore[key] = JSON.parse(JSON.stringify(version.snapshot))
      activateJourney(key)
      vm.currentVersionNumber = version.versionNumber
    }
    vm.onJourneyGenerated = function (journeyConfig) {
      const previousKey = vm.currentJourneyKey
      const key = journeyConfig.name || `generated_${Object.keys(journeyStore).length + 1}`
      journeyConfig._key = key
      // The chat always edits/replaces whatever journey is currently open, never adds
      // a sibling — drop the old dropdown entry whenever the (re)generated JSON's name
      // gives it a different store key (e.g. sample's "(Sample)" display key, or the
      // untitled placeholder getting its first real name).
      if (previousKey && previousKey !== key) {
        delete journeyStore[previousKey]
        renameJourney(previousKey, key)
        renameJourneyComments(previousKey, key)
      }
      journeyStore[key] = journeyConfig
      rebuildJourneyDropdown(key)
      activateJourney(key)
    }
    vm.onCreateNew = function () {
      const key = UNTITLED_KEY
      journeyStore[key] = { name: key, description: '', type: 'journey', stages: {}, _key: key }
      rebuildJourneyDropdown(key)
      activateJourney(key)
    }
    vm.onViewModeChange = function (mode) {
      const prevMode = vm.viewMode
      vm.viewMode = mode
      if (mode === 'client') {
        if (selectedCardName) clearSelection()
        if (vm.presentationData) renderPhaseCards()
        else fetchPresentation()
      } else {
        if (prevMode === 'client') clearPhaseSelection()
        renderCards()
        requestAnimationFrame(() => requestAnimationFrame(drawLines))
      }
    }
    vm.onSelModeChange = function (mode) {
      vm.selMode = mode
      if (selectedCardName) selectCard(selectedCardName, true)
      if (selectedPhaseName) selectPhaseCard(selectedPhaseName, true)
    }
    vm.onSelectStageFromExplorer = function (name) {
      selectCard(name)
    }
    vm.onDeselectStage = function () {
      clearSelection()
    }
    vm.onUpdateStageMessage = function ({ stageName, lang, text }) {
      updateStageMessage(stageName, lang, text)
    }
    vm.onUpdateStageTopics = function ({ stageName, topics }) {
      updateStageTopics(stageName, topics)
    }
    vm.onSelectPhaseFromExplorer = function (id) {
      selectPhaseCard(id)
    }
    vm.onDeselectPhase = function () {
      clearPhaseSelection()
    }
    vm.onCollapseAll = function () { if (vm.viewMode !== 'client') toggleAll(true) }
    vm.onExpandAll = function () { if (vm.viewMode !== 'client') toggleAll(false) }
    vm.onResetLayout = function () {
      if (vm.viewMode === 'client') { renderPhaseCards(); return }
      reLayout(true); setTimeout(fitToScreen, 350)
    }
    vm.onFitScreen = fitToScreen
    vm.onZoomIn = function () { zoomBy(0.15) }
    vm.onZoomOut = function () { zoomBy(-0.15) }
    vm.onToggleScrollZoom = function () { vm.scrollZoomEnabled = !vm.scrollZoomEnabled }

    // Make the bundled sample journey available in the dropdown, but don't auto-load it —
    // the page opens empty until the user picks or creates a journey.
    const sampleJourney = JSON.parse(JSON.stringify(sampleJourneyData))
    const sampleKey = `${sampleJourney.name || 'aids_awareness_survey'} (Sample)`
    sampleJourney._key = sampleKey
    journeyStore[sampleKey] = sampleJourney
    rebuildJourneyDropdown('')
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');

.journey-flow-viewer {
  --topbar-h:48px;
  --bg:#f5f6f8;--card:#ffffff;--card-border:#e1e3e8;--card-hl:#3b82f6;
  --card-label-bg:#f1f2f5;--text:#1a1d23;--text-muted:#6b7280;--text-faint:#9aa0ab;
  --btn-bg:#ffffff;--btn-border:#dde0e6;
  --btn-hl-bg:rgba(59,130,246,0.08);--btn-hl-border:rgba(59,130,246,0.4);--btn-hl-text:#1d4ed8;
  --line:#c7cbd3;--line-hl:#3b82f6;--accent:#3b82f6;--accent-light:#1d4ed8;--accent-strong:#0a5dc2;
  --loop-bg:rgba(99,102,241,0.08);--loop-text:#4f46e5;--loop-border:rgba(99,102,241,0.25);
  --dot-color:#e2e4e9;
  --ft-bg:rgba(234,88,12,0.08);--ft-border:rgba(234,88,12,0.3);--ft-text:#c2410c;
  --pf-bg:rgba(124,58,237,0.08);--pf-border:rgba(124,58,237,0.3);--pf-text:#7c3aed;
  position:fixed;inset:0;overflow:hidden;background:var(--bg);
  font-family:'Inter',system-ui,sans-serif;color:var(--text);font-size:13px;-webkit-font-smoothing:antialiased;
  z-index:1000;
}
.journey-flow-viewer *{box-sizing:border-box;margin:0}
.journey-flow-viewer ::selection{background:rgba(59,130,246,0.25)}

/* Empty state */
.journey-flow-viewer .empty-hint{
  position:absolute;top:var(--topbar-h);left:0;right:0;bottom:0;display:flex;align-items:center;justify-content:center;
  pointer-events:none;z-index:5;
}
.journey-flow-viewer .empty-hint.hidden{display:none}
.journey-flow-viewer .empty-hint div{
  text-align:center;color:var(--text-faint);font-size:13px;line-height:1.7;
}
.journey-flow-viewer .empty-hint .big{font-size:32px;margin-bottom:12px;opacity:0.3}

/* Canvas */
.journey-flow-viewer .canvas-wrap{
  position:absolute;top:var(--topbar-h);left:0;right:0;bottom:0;overflow:hidden;cursor:grab;
  background:radial-gradient(circle at 1px 1px,var(--dot-color) 1px,transparent 0);
  background-size:24px 24px;
}
.journey-flow-viewer .canvas-wrap.grabbing{cursor:grabbing}
.journey-flow-viewer .canvas{position:absolute;transform-origin:0 0;will-change:transform}

/* Floating canvas controls (bottom-left / top-left, revealed on hover / on selection) */
.journey-flow-viewer .canvas-controls,
.journey-flow-viewer .view-controls,
.journey-flow-viewer .selection-controls{
  position:fixed;left:calc(var(--sidebar-w, 40px) + 16px);z-index:60;
  display:flex;align-items:center;gap:6px;
  padding:5px 8px;border-radius:10px;
  background:var(--card);border:1px solid var(--card-border);
  box-shadow:0 4px 16px rgba(0,0,0,0.12);
  transition:left .15s ease;
}
.journey-flow-viewer .canvas-controls{
  bottom:16px;
  opacity:0;pointer-events:none;transition:left .15s ease,opacity .15s;
}
.journey-flow-viewer .view-controls{top:calc(var(--topbar-h) + 16px)}
.journey-flow-viewer .vc-version-badge{
  font-family:'JetBrains Mono',monospace;font-size:11px;font-weight:700;color:#1d4ed8;
  background:rgba(59,130,246,0.14);padding:4px 9px;border-radius:999px;white-space:nowrap;
}
.journey-flow-viewer .vc-comments-btn{
  display:flex;align-items:center;gap:7px;padding:6px 12px;border-radius:8px;
  border:1px solid var(--card-border);background:var(--card);color:var(--text);
  font-size:12.5px;font-weight:600;cursor:pointer;font-family:inherit;white-space:nowrap;
}
.journey-flow-viewer .vc-comments-btn:hover{border-color:var(--btn-hl-border)}
.journey-flow-viewer .vc-comments-count{
  background:rgba(59,130,246,0.14);color:#1d4ed8;font-size:10.5px;font-weight:700;
  padding:1px 7px;border-radius:999px;
}
.journey-flow-viewer .canvas-wrap:hover ~ .canvas-controls,
.journey-flow-viewer .canvas-controls:hover{opacity:1;pointer-events:auto}
.journey-flow-viewer .selection-controls{bottom:64px}

/* Top-right editor actions (always visible, shifts with the Stage Explorer panel) */
.journey-flow-viewer .editor-actions{
  position:fixed;top:calc(var(--topbar-h) + 16px);right:calc(var(--explorer-w, 40px) + 16px);z-index:60;
  display:flex;align-items:center;gap:6px;
  transition:right .15s ease;
}
.journey-flow-viewer .ea-btn{
  padding:5px 12px;border-radius:6px;border:1px solid var(--card-border);
  background:var(--card);color:var(--text-muted);font-size:11.5px;font-weight:500;
  cursor:pointer;font-family:inherit;transition:all .12s;
  display:flex;align-items:center;gap:5px;white-space:nowrap;
}
.journey-flow-viewer .ea-icon-btn{
  width:30px;height:30px;border-radius:50%;border:1px solid var(--card-border);
  background:var(--card);color:var(--text-muted);cursor:pointer;transition:all .12s;
  display:flex;align-items:center;justify-content:center;flex-shrink:0;
}
.journey-flow-viewer .ea-icon-btn:hover{border-color:var(--btn-hl-border);color:var(--text);background:var(--btn-hl-bg)}
.journey-flow-viewer .ea-btn:hover{border-color:var(--btn-hl-border);color:var(--text)}
.journey-flow-viewer .ea-save{background:rgba(59,130,246,0.12);border-color:var(--accent);color:var(--accent-light)}
.journey-flow-viewer .ea-save:hover{background:rgba(59,130,246,0.18)}
.journey-flow-viewer .tb-btn{
  width:28px;height:28px;border-radius:6px;border:1px solid var(--card-border);
  background:var(--card);color:var(--text-muted);cursor:pointer;
  display:flex;align-items:center;justify-content:center;font-size:14px;
}
.journey-flow-viewer .tb-btn:hover{border-color:var(--btn-hl-border);color:var(--text)}
.journey-flow-viewer .tb-btn.active{border-color:var(--accent);color:var(--accent-light);background:rgba(59,130,246,0.12)}
.journey-flow-viewer .tb-sep{width:1px;height:18px;background:var(--card-border);margin:0 2px}
.journey-flow-viewer .zoom-info{font-size:11px;color:var(--text-faint);min-width:36px;text-align:center}
.journey-flow-viewer .mode-btn{
  padding:0 8px;height:28px;border-radius:6px;border:1px solid var(--card-border);
  background:var(--card);color:var(--text-muted);font-size:10px;font-weight:500;
  cursor:pointer;font-family:inherit;
}
.journey-flow-viewer .mode-btn:hover{border-color:var(--btn-hl-border);color:var(--text)}
.journey-flow-viewer .mode-btn.active{border-color:var(--accent);color:var(--accent-light)}
.journey-flow-viewer .card-ctx-menu{
  position:fixed;z-index:1100;background:var(--card);border:1px solid var(--card-border);
  border-radius:9px;box-shadow:0 12px 32px rgba(0,0,0,0.18);padding:4px;min-width:200px;
}
.journey-flow-viewer .card-ctx-item{
  width:100%;text-align:left;background:transparent;border:none;border-radius:6px;
  padding:8px 10px;font-size:12.5px;font-weight:500;color:var(--text);cursor:pointer;
  display:flex;align-items:center;gap:6px;font-family:inherit;
}
.journey-flow-viewer .card-ctx-item:hover{background:var(--card-label-bg);color:var(--accent-light)}
.journey-flow-viewer svg.lines{position:absolute;top:0;left:0;overflow:visible;pointer-events:none}
.journey-flow-viewer svg.lines path{fill:none;stroke:var(--line);stroke-width:1.5;transition:stroke .12s;pointer-events:stroke;cursor:pointer}
.journey-flow-viewer svg.lines path:hover{stroke:var(--line-hl);stroke-width:2.5}
.journey-flow-viewer svg.lines path.hl{stroke:var(--line-hl);stroke-width:2}

/* Cards */
.journey-flow-viewer .card{
  position:absolute;width:260px;
  background:var(--card);border:1px solid var(--card-border);
  border-radius:10px;overflow:visible;
  transition:top .3s cubic-bezier(.4,0,.2,1),left .3s cubic-bezier(.4,0,.2,1),border-color .12s;
}
.journey-flow-viewer .card.hl{border-color:var(--card-hl)}
.journey-flow-viewer .card.selected{border-color:var(--accent);box-shadow:0 0 0 2px rgba(59,130,246,0.25)}
.journey-flow-viewer .card.neighbor{border-color:rgba(59,130,246,0.5);box-shadow:0 0 0 1px rgba(59,130,246,0.15)}
.journey-flow-viewer .card.dimmed{opacity:0.25}
.journey-flow-viewer .card.hidden-card{display:none}
.journey-flow-viewer svg.lines path.conn-hl{stroke:var(--line-hl);stroke-width:2.5}
.journey-flow-viewer svg.lines circle.conn-hl{fill:var(--line-hl)}
.journey-flow-viewer .card.dragging{z-index:50;opacity:0.92;border-color:var(--accent);transition:none}
/* Flow-view cards wrap their clip-path shape in an inner .flow-node so the comment
   badge (a sibling, not a descendant of the clipped shape) never gets clipped by it.
   The outer .card becomes an invisible hit/positioning box; selection/hover states
   have to be re-applied to the inner shape explicitly since it no longer inherits
   the outer border/box-shadow visually (clip-path repaints its own box). */
.journey-flow-viewer .card.flow-node-wrap{background:transparent;border-color:transparent}
.journey-flow-viewer .card.flow-node-wrap.hl .flow-node{border-color:var(--card-hl)}
.journey-flow-viewer .card.flow-node-wrap.selected .flow-node{border-color:var(--accent);box-shadow:0 0 0 2px rgba(59,130,246,0.25)}
.journey-flow-viewer .card.flow-node-wrap.neighbor .flow-node{border-color:rgba(59,130,246,0.5);box-shadow:0 0 0 1px rgba(59,130,246,0.15)}
.journey-flow-viewer .card.flow-node-wrap.dragging .flow-node{border-color:var(--accent)}
.journey-flow-viewer .card-cmt-badge{
  position:absolute;top:-9px;right:-8px;z-index:2;
  background:#2563EB;color:#fff;font-size:10px;font-weight:700;
  padding:2px 7px;border-radius:999px;box-shadow:0 1px 4px rgba(0,0,0,0.25);
  cursor:pointer;white-space:nowrap;transition:filter .1s;
}
.journey-flow-viewer .card-cmt-badge:hover{filter:brightness(1.15)}

/* Flow View — compact shaped nodes (rect/hexagon/diamond/pill) instead of full cards */
.journey-flow-viewer .flow-node{
  width:260px;box-sizing:border-box;cursor:grab;user-select:none;
  display:flex;align-items:center;justify-content:center;text-align:center;
  border-width:1.5px;border-style:solid;
}
.journey-flow-viewer .flow-node:active{cursor:grabbing}
.journey-flow-viewer .flow-node-label{padding:4px 8px}
.journey-flow-viewer .flow-node-emoji{margin-right:5px}
.journey-flow-viewer .flow-node-name{font-family:'JetBrains Mono',monospace;font-size:12px;font-weight:600}
.journey-flow-viewer .flow-node-descr{font-size:10px;font-style:italic;color:inherit;opacity:0.65;margin-top:3px}
.journey-flow-viewer .flow-shape-rect{min-height:60px;border-radius:9px;background:#DBEAFE;border-color:#3B82F6;color:#1E3A8A}
.journey-flow-viewer .flow-shape-hex{
  min-height:84px;padding:0 30px;background:#D1FAE5;border-color:#0D9488;color:#065F56;
  clip-path:polygon(9% 0%,91% 0%,100% 50%,91% 100%,9% 100%,0% 50%);
}
.journey-flow-viewer .flow-shape-diamond{
  min-height:150px;background:#CCFBF1;border-color:#0D9488;color:#0F766E;
  clip-path:polygon(50% 6%,94% 50%,50% 94%,6% 50%);
}
.journey-flow-viewer .flow-shape-pill{min-height:56px;border-radius:999px;background:#E5E7EB;border-color:#6B7280;color:#374151}
.journey-flow-viewer .flow-edge-label{font-size:10.5px;font-weight:600;fill:var(--text-muted);text-anchor:middle;dominant-baseline:middle;pointer-events:none}
.journey-flow-viewer .flow-edge-label-bg{fill:var(--bg);pointer-events:none}
.journey-flow-viewer .card-head{
  padding:7px 10px;background:var(--card-label-bg);
  border-bottom:1px solid var(--card-border);
  border-radius:10px 10px 0 0;
  display:flex;align-items:center;gap:6px;cursor:grab;user-select:none;
}
.journey-flow-viewer .card-head:active{cursor:grabbing}
.journey-flow-viewer .card.collapsed .card-head{border-bottom:none;border-radius:10px}
.journey-flow-viewer .card-head .icon{font-size:13px;color:var(--text-faint)}
.journey-flow-viewer .card-head .label{font-size:11px;font-weight:500;color:var(--text-muted);flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.journey-flow-viewer .card-head .stage-name{font-family:'JetBrains Mono',monospace;font-size:10px;color:var(--text-faint);max-width:100px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.journey-flow-viewer .card-head .chevron{color:var(--text-faint);font-size:14px;transition:transform .15s;flex-shrink:0}
.journey-flow-viewer .card.collapsed .chevron{transform:rotate(-90deg)}
.journey-flow-viewer .card-body{padding:10px 11px}
.journey-flow-viewer .card.collapsed .card-body{display:none}
.journey-flow-viewer .card-msg{font-size:12.5px;line-height:1.55;color:var(--text);white-space:pre-wrap;word-break:break-word}
.journey-flow-viewer .phase-card-title{font-size:13px;font-weight:700;color:var(--text);line-height:1.3}
.journey-flow-viewer .phase-card-desc{font-size:11.5px;color:var(--text-muted);margin-top:5px;line-height:1.5}
.journey-flow-viewer .phase-card-count{font-size:10.5px;color:var(--text-faint);margin-top:7px}
.journey-flow-viewer .stage-kv{display:flex;flex-direction:column;gap:4px}
.journey-flow-viewer .stage-kv-row{display:flex;gap:8px;font-size:11.5px;align-items:baseline}
.journey-flow-viewer .stage-kv-row .k{color:var(--text-faint);min-width:78px;flex-shrink:0}
.journey-flow-viewer .stage-kv-row .v{font-family:'JetBrains Mono',monospace;color:var(--text);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.journey-flow-viewer .card-btns{padding:0 8px 8px;display:flex;flex-direction:column;gap:4px}
.journey-flow-viewer .card.collapsed .card-btns{padding-top:8px}
.journey-flow-viewer .cbtn{
  padding:7px 12px;border-radius:8px;border:1px solid var(--btn-border);
  background:var(--btn-bg);font-size:12px;color:var(--text);font-weight:500;
  text-align:left;cursor:default;display:flex;align-items:center;gap:8px;
  transition:all .12s;line-height:1.35;position:relative;
}
.journey-flow-viewer .cbtn .cdot{width:6px;height:6px;border-radius:50%;background:var(--accent);flex-shrink:0;opacity:0.4;transition:opacity .1s}
.journey-flow-viewer .cbtn:hover,.journey-flow-viewer .cbtn.hl{border-color:var(--btn-hl-border);background:var(--btn-hl-bg);color:var(--btn-hl-text)}
.journey-flow-viewer .cbtn:hover .cdot,.journey-flow-viewer .cbtn.hl .cdot{opacity:1}
.journey-flow-viewer .card-input{padding:7px 10px;border-radius:8px;border:1px solid var(--ft-border);background:var(--ft-bg);font-size:11.5px;color:var(--ft-text);display:flex;align-items:center;gap:5px}
.journey-flow-viewer .card-pf{padding:7px 10px;border-radius:8px;border:1px solid var(--pf-border);background:var(--pf-bg);font-size:11.5px;color:var(--pf-text);display:flex;align-items:center;gap:5px}
.journey-flow-viewer .card-loop{padding:7px 10px;border-radius:8px;border:1px solid var(--loop-border);background:var(--loop-bg);font-size:11.5px;color:var(--loop-text);display:flex;align-items:center;gap:5px}

/* Connection hover tooltip */
.journey-flow-viewer .conn-tooltip{
  position:fixed;z-index:1000;pointer-events:none;
  background:var(--card);border:1px solid var(--card-border);
  color:var(--text);font-size:11.5px;font-weight:500;line-height:1.4;
  padding:6px 10px;border-radius:7px;
  box-shadow:0 4px 16px rgba(0,0,0,0.15);
  display:flex;align-items:center;gap:6px;
  max-width:240px;white-space:normal;word-break:break-word;
  opacity:0;visibility:hidden;transition:opacity .08s;
  left:0;top:0;
}
.journey-flow-viewer .conn-tooltip.show{opacity:1;visibility:visible}
.journey-flow-viewer .conn-tooltip .ct-icon{font-size:12px;flex-shrink:0}
</style>



// WEBPACK FOOTER //
// CreateNewJourney.vue?299e9606